import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircleIcon, FileArrowDownIcon } from '@/components/icons';
import { useI18n } from '@/i18n/I18nProvider';
import { useToastNotifications } from '@/lib/hooks/use-toast-notifications';
import { authenticatedFetcher } from '@/lib/swr';
import { downloadBase64File } from '@/lib/helpers';
import { DownloadPartnershipCharterResponse, useDownloadCharter } from '@/lib/hooks/useDownloadCharter';
import { PP_BASE_URL } from '@/lib/env';
import type { ApiResponse } from '@/types/api-response';

interface CharterSignatureViewProps {
  studentNumber: string | null;
  academicYear?: string;
  schoolName: string;
  studentFullName: string;
  parentFullName: string;
  isSigned: boolean;
  onSignedSuccess: () => void;
}

export function CharterSignatureView({
  studentNumber,
  academicYear,
  schoolName,
  studentFullName,
  parentFullName,
  isSigned,
  onSignedSuccess,
}: CharterSignatureViewProps) {
  const { t, locale } = useI18n();
  const toast = useToastNotifications();
  const { downloadCharter, isDownloading } = useDownloadCharter();

  const [isAgreed, setIsAgreed] = React.useState(false);
  const [isSigning, setIsSigning] = React.useState(false);

  const today = React.useMemo(() => {
    try {
      return new Date().toLocaleDateString('en-US');
    } catch {
      return new Date().toLocaleDateString();
    }
  }, []);

  const handleSign = React.useCallback(async () => {
    if (!isAgreed || isSigning || !studentNumber) return;

    setIsSigning(true);
    try {
      const ppBaseUrl = PP_BASE_URL;
      if (!ppBaseUrl) {
        throw new Error('API base URL not configured');
      }

      // POST request payload to backend to sign charter
      const response = await authenticatedFetcher<ApiResponse<DownloadPartnershipCharterResponse>>(`${ppBaseUrl}/charter`, {
        method: 'POST',
        body: JSON.stringify({
          studentNumber,
          academicYear,
          locale,
        }),
      });

      toast.success(
        locale === 'ar' ? 'تم بنجاح' : 'Success',
        locale === 'ar' ? 'تم توقيع الميثاق بنجاح.' : 'Charter signed successfully.'
      );

      // Automatically download the returned Base64 PDF without an extra API call
      downloadBase64File(response.data?.attachment01!, `partnership-charter-${studentNumber}.pdf`, 'application/pdf');
      
      onSignedSuccess();
    } catch (error: any) {
      console.error('Error signing charter:', error);
      toast.error(
        locale === 'ar' ? 'خطأ' : 'Error',
        error?.title || (locale === 'ar' ? 'تعذر توقيع الميثاق.' : 'Failed to sign charter.')
      );
    } finally {
      setIsSigning(false);
    }
  }, [isAgreed, isSigning, studentNumber, academicYear, locale, toast, onSignedSuccess, downloadCharter]);

  const handleDownload = React.useCallback(() => {
    downloadCharter({ studentNumber, academicYear });
  }, [downloadCharter, studentNumber, academicYear]);

  return (
    <div className="space-y-6">
      <Card className="border shadow-sm">
        <CardHeader className="rounded-t-xl bg-primary text-primary-foreground">
          <CardTitle className="text-lg flex items-center">
            <CheckCircleIcon className="me-2" />
            {t.parentConduct.signatureSection.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="bg-muted border rounded-lg p-3 mb-4">
            <h4 className="font-medium text-foreground mb-2 text-sm">
              {t.parentConduct.signatureSection.summaryTitle}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              <div>
                <span className="font-medium">{t.parentConduct.signatureSection.school}</span> {schoolName}
              </div>
              <div>
                <span className="font-medium">{t.parentConduct.signatureSection.student}</span> {studentFullName}
              </div>
              <div>
                <span className="font-medium">{t.parentConduct.signatureSection.parent}</span> {parentFullName}
              </div>
            </div>
          </div>

          {isSigned && (
            <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-500 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <CheckCircleIcon size={28} className="me-2" />
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-green-800 dark:text-green-300 mb-2">
                    {locale === 'ar' ? 'تم توقيع الميثاق بنجاح' : 'Charter Already Signed'}
                  </h3>
                  <p className="text-sm text-green-700 dark:text-green-400 mb-4">
                    {locale === 'ar'
                      ? 'لقد قمت بالفعل بتوقيع ميثاق الشراكة بين المدرسة وولي الأمر. يمكنك تحميل نسخة من الميثاق الموقع في أي وقت.'
                      : 'You have already signed the partnership charter between the school and parent. You can download a copy of the signed charter at any time.'}
                  </p>
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={handleDownload}
                      disabled={isDownloading}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-all shadow-sm"
                    >
                      <FileArrowDownIcon />
                      {locale === 'ar' ? 'تحميل الميثاق الموقع' : 'Download Signed Charter'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isSigned && (
            <div className="p-4 bg-accent/20 border border-accent rounded-lg">
              <div className="text-xs text-muted-foreground leading-relaxed mb-4">
                <span className="font-medium text-foreground block mb-2">
                  {t.parentConduct.signatureSection.parentDeclaration}
                </span>
                {t.parentConduct.signatureSection.declarationText}
              </div>

              <div className="flex items-start gap-3 mt-4 p-3 bg-background rounded border">
                <input
                  type="checkbox"
                  id="agree-checkbox"
                  checked={isAgreed}
                  onChange={(e) => setIsAgreed(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  disabled={isSigning}
                />
                <label htmlFor="agree-checkbox" className="text-sm text-foreground cursor-pointer">
                  {locale === 'ar'
                    ? 'أوافق على جميع بنود وشروط ميثاق الشراكة بين المدرسة وولي الأمر وأتعهد بالالتزام بها.'
                    : 'I agree to all terms and conditions of the partnership charter between the school and parent.'}
                </label>
              </div>

              <div className="mt-4 flex justify-start">
                <button
                  type="button"
                  onClick={handleSign}
                  disabled={!isAgreed || isSigning || !studentNumber}
                  className={`aegov-btn ${!isAgreed || isSigning || !studentNumber ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : ''}`}
                >
                  {isSigning
                    ? locale === 'ar'
                      ? 'جاري التوقيع...'
                      : 'Signing...'
                    : locale === 'ar'
                      ? 'توقيع الميثاق'
                      : 'Sign Charter'}
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 items-end gap-4 mt-4">
            <div className="flex flex-col items-center justify-center border-2 border-secondary rounded-lg py-6 bg-secondary/10">
              <CheckCircleIcon />
              <div className="text-secondary-foreground text-xs mb-1">
                {t.parentConduct.signatureSection.ministryStamp}
              </div>
              <div className="text-lg font-bold text-secondary-foreground">
                {t.parentConduct.signatureSection.approved}
              </div>
            </div>

            <div className="flex flex-col items-center justify-center border-2 border-secondary rounded-lg py-6 bg-secondary/10">
              <div className="block text-xs font-medium text-foreground mb-2">
                {t.parentConduct.signatureSection.date}
              </div>
              <div className="bg-muted text-center font-medium text-sm rounded px-3 py-2">{today}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
